import React, { useState, useMemo, useRef } from "react"
import { useShopify } from "../../../hooks/useShopify"
import { useAnalytics } from "../../../hooks/useAnalytics"
import { useTemplate } from "../../../hooks/useTemplate"
import { useCheckoutContext } from "../../../hooks/useCheckout"
import { useShopContext } from "../../../hooks/useShop"
import { useProductContent } from "../../../hooks/useProductContent"

export const withFeaturedProductCard =
  Component =>
  ({ name = "FeaturedProductCard", isOneHalf = false, oneHalfProduct = null, product: rawProduct, index, type }) => {
    const { imageNormaliser, formatProduct, productNormaliser, formatBadge } = useShopify()
    const { trackProductImpression, trackProductClick, VisibilitySensor } = useAnalytics()
    const {
      collection: { featureImageIndex },
    } = useTemplate()
    const { shop } = useShopContext()
    const { checkout } = useCheckoutContext()
    const itemRef = useRef(null)
    const { getProductsByTag } = useProductContent()
    //eslint-disable-next-line react-hooks/exhaustive-deps
    const product = useMemo(() => productNormaliser(rawProduct), [rawProduct, shop, checkout])
    const selectedProduct = useMemo(() => product.id, [product])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const formattedProduct = useMemo(() => formatProduct(product), [product])
    const productCategory = formattedProduct.productCategory
    const isPackProduct = useMemo(
      () =>
        product.productType === "Pack" ||
        product?.tags?.find(tag => tag?.toLowerCase()?.includes("producttype:") && tag?.toLowerCase()?.includes("pack")),
      [product]
    )
    const isInStock = product.variants.filter(p => {
      return p.inventory_quantity > 0
    })
    const selectedVariant = useMemo(
      () => (isPackProduct ? product.variants.find(v => v.title.includes("Default")) : isInStock.length ? isInStock[0] : product.variants[0]),
      [product?.variants, isPackProduct, isInStock]
    )
    const groupTag = product.tags.find(tag => tag.startsWith("productGroup:"))
    const productColours = getProductsByTag([groupTag])
    const sibilingProducts = productColours.sort((a, b) => a.title.localeCompare(b.title))
    // Sanity may have marked a specific image that will appear on the product card
    // The settings can be found from "template/collection"
    const imageIndex = useMemo(
      () =>
        !featureImageIndex
          ? 0
          : featureImageIndex !== "last" && product?.images?.length && product?.images?.length >= parseInt(featureImageIndex)
          ? parseInt(featureImageIndex) - 1
          : product?.images?.length && product?.images?.length > 1
          ? product?.images?.length - 1
          : 0,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [featureImageIndex, product]
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const image = useMemo(() => imageNormaliser(product?.images?.[imageIndex], 500), [product])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const hoverImage = useMemo(() => imageNormaliser(product?.images?.[0], 500), [product])
    let outOfStock = false,
      badgeFormat = null
    const maxVals = formattedProduct?.variants.reduce((max, p) => (max.inventory_quantity > p.inventory_quantity ? max : p))
    outOfStock = !maxVals.available
    badgeFormat = formatBadge(formattedProduct, maxVals)

    const [backToStockActive, setBackToStockActive] = useState(false)
    const [displayHover, setDisplayHover] = useState(false)

    Component.displayName = name
    return useMemo(() => {
      const handleClick = () => {
        sessionStorage.setItem("prevURL", window.location.href)
        trackProductClick(product, formattedProduct.colour, index, "Featured Products")
      }
      const handleHover = state => {
        return product?.images?.length > 1 && setDisplayHover(state)
      }
      return (
        <VisibilitySensor onChange={visible => visible && trackProductImpression(product, index, "Featured Products")}>
          <Component
            isOneHalf={isOneHalf}
            product={product}
            colours={sibilingProducts}
            productCategory={productCategory}
            image={isOneHalf ? oneHalfProduct.image : image}
            hoverImage={hoverImage}
            link={formattedProduct.link}
            title={formattedProduct.title}
            saleTag={formattedProduct.saleTag}
            material={formattedProduct.material}
            handleClick={handleClick}
            handleHover={handleHover}
            displayHover={displayHover}
            itemRef={itemRef}
            sizeTag={formattedProduct.sizeTag}
            shapeTag={formattedProduct.shapeTag}
            shapeIcon={formattedProduct.shapeIcon}
            badge={badgeFormat}
            selectedVariant={selectedVariant}
            selectedProduct={selectedProduct}
            isPackProduct={isPackProduct}
            type={type}
            outOfStock={outOfStock}
            active={backToStockActive}
            setActive={setBackToStockActive}
          />
        </VisibilitySensor>
      )
    }, [
      isOneHalf,
      oneHalfProduct,
      product,
      sibilingProducts,
      image,
      hoverImage,
      formattedProduct.link,
      formattedProduct.title,
      formattedProduct.saleTag,
      formattedProduct.material,
      formattedProduct.sizeTag,
      formattedProduct.shapeTag,
      formattedProduct.shapeIcon,
      formattedProduct?.variants,
      formattedProduct.colour,
      displayHover,
      formatBadge,
      selectedVariant,
      selectedProduct,
      isPackProduct,
      type,
      outOfStock,
      backToStockActive,
      trackProductClick,
      index,
      trackProductImpression,
    ])
  }
